import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import SectionLoader from '../Loader/SectionLoader';
import Paragraph from '../Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import DownloadIcon from '../Common/Icons/DownloadIcon';

const getButtonContainerPadding = () => '16px 24px';

const Container = styled.div`
  position: relative;

  @media only screen and (max-width: 750px) {
    padding: 0;
  }
`;

const ButtonContainer = styled.div`
  position: sticky;
  bottom: 0;
  background-color: ${themeColors.colorBackgroundPrimary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ variant, noPadding }) =>
    getButtonContainerPadding(variant, noPadding)};

  @media only screen and (max-width: 500px) {
    flex-direction: column-reverse;
    gap: 25px;
  }
`;

const EndChecker = styled.div`
  opacity: 0;
`;

const DownloadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  cursor: ${({ isDownloading }) => (isDownloading ? 'default' : 'pointer')};
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 0 24px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const DownloadSpinner = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid ${themeColors.colorLinkPrimary};
  border-top-color: transparent;
  animation: 0.8s ${spin} linear infinite;
`;

const NoteContainer = styled.div`
  position: sticky;
  bottom: 30px;
  padding-top: 16px;
  background-color: ${themeColors.colorBackgroundPrimary};
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 750px) {
    bottom: 42px;
  }
`;

const PrintModal = ({
  isOpen,
  variant = 'lg',
  onClose,
  children,
  buttonText,
  onCloseButton,
  hidePrint,
  shouldScrollDown,
  isLoading,
  isEquityAgreement,
  onDownload,
  downloadLabel,
  isDownloading,
  hideHeaderShadow,
  closeIconRight,
}) => {
  const { t } = useTranslation();
  const componentRef = useRef();
  const endCheckerRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!endCheckerRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(endCheckerRef.current);

    return () => observer.disconnect();
  }, [isOpen]);

  const handlePrint = () => window.print();

  return (
    <Modal
      isOpen={isOpen}
      size={variant === 'xl' ? 'lg' : variant}
      close={onClose}
      closeIconVisible
      hideHeaderShadow={hideHeaderShadow}
      closeIconRight={closeIconRight}
    >
      <SectionLoader isLoading={isLoading}>
        <Container variant={variant} ref={componentRef}>
          {children}
          {onDownload && !isLoading && (
            <DownloadContainer
              onClick={isDownloading ? undefined : onDownload}
              isDownloading={isDownloading}
            >
              {isDownloading ? (
                <DownloadSpinner />
              ) : (
                <DownloadIcon
                  w="20px"
                  h="20px"
                  color={themeColors.colorLinkPrimary}
                />
              )}
              <Paragraph
                variant="normal"
                fontWeight={600}
                color={themeColors.colorLinkPrimary}
              >
                {downloadLabel || t('common.download')}
              </Paragraph>
            </DownloadContainer>
          )}
          <EndChecker ref={endCheckerRef} />
          {isEquityAgreement && (
            <NoteContainer>
              {!inView && (
                <Paragraph color={themeColors.colorError} fontSize={12}>
                  {t('equityAgreement.note')}
                </Paragraph>
              )}
            </NoteContainer>
          )}
          <ButtonContainer variant={variant}>
            {buttonText !== null && (
              <Button
                variant="primary"
                disabled={
                  isLoading || isDownloading || (shouldScrollDown && !inView)
                }
                label={buttonText || t('common.ok')}
                onClick={onCloseButton}
              />
            )}
            {!hidePrint && (
              <Button
                variant="primaryOutlined"
                label={t('common.printDownload')}
                onClick={handlePrint}
              />
            )}
          </ButtonContainer>
        </Container>
      </SectionLoader>
    </Modal>
  );
};

PrintModal.propTypes = {
  children: PropTypes.node,
  buttonText: PropTypes.string,
  isOpen: PropTypes.bool,
  variant: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  onClose: PropTypes.func,
  onCloseButton: PropTypes.func,
  hidePrint: PropTypes.bool,
  shouldScrollDown: PropTypes.bool,
  isLoading: PropTypes.bool,
  isEquityAgreement: PropTypes.bool,
  onDownload: PropTypes.func,
  downloadLabel: PropTypes.string,
  isDownloading: PropTypes.bool,
  hideHeaderShadow: PropTypes.bool,
  closeIconRight: PropTypes.bool,
};

export default PrintModal;
